import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import LinkPreviewPanel from '@/components/LinkPreviewPanel';
import SourceTrustBadge from '@/components/SourceTrustBadge';
import MatterIntelligenceCard from '@/components/MatterIntelligenceCard';
import { toast } from 'sonner';
import {
  buildSourceLanguageLabel,
  chooseVisibleSourceText,
  normalizeDisplayLanguage,
} from '@/lib/sourceRendering';
import {
  Upload, Trash2, FileText, Loader2, Database, Search, X,
  RefreshCw, AlertCircle, CheckCircle2,
  Image as ImageIcon, FileType2, Eye, RotateCcw,
  BookOpen, Globe, Layers, HardDrive, Pencil, Save, StickyNote,
  Sparkles, Languages,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WS_URL = (process.env.REACT_APP_BACKEND_URL || 'http://127.0.0.1:8001')
  .replace(/^http/, 'ws')
  .replace(/^https/, 'wss');

function getKnowledgePreviewUrl(fileId) {
  return `${API}/knowledge/files/${fileId}/preview`;
}

// ── Badge colours per document type ─────────────────────────────────────────
const DOC_TYPE_STYLES = {
  FIR:               { bg: 'rgba(239,68,68,0.12)',   color: '#ef4444' },
  CHARGESHEET:       { bg: 'rgba(249,115,22,0.12)',  color: '#f97316' },
  JUDGMENT:          { bg: 'rgba(168,85,247,0.12)',  color: '#a855f7' },
  ORDER:             { bg: 'rgba(20,184,166,0.12)',  color: '#14b8a6' },
  AFFIDAVIT:         { bg: 'rgba(245,158,11,0.12)',  color: '#f59e0b' },
  CONTRACT:          { bg: 'rgba(59,130,246,0.12)',  color: '#3b82f6' },
  PETITION:          { bg: 'rgba(99,102,241,0.12)',  color: '#6366f1' },
  BAIL_APPLICATION:  { bg: 'rgba(236,72,153,0.12)', color: '#ec4899' },
  NOTICE:            { bg: 'rgba(234,179,8,0.12)',   color: '#eab308' },
  PLAINT:            { bg: 'rgba(6,182,212,0.12)',   color: '#06b6d4' },
  WITNESS_STATEMENT: { bg: 'rgba(16,185,129,0.12)',  color: '#10b981' },
  OTHER:             { bg: 'rgba(148,163,184,0.1)',  color: 'var(--text-muted)' },
};

const LANG_LABELS = {
  en: 'English', hi: 'Hindi', pa: 'Punjabi', ta: 'Tamil',
  te: 'Telugu', kn: 'Kannada', ml: 'Malayalam', bn: 'Bengali',
  gu: 'Gujarati', mr: 'Marathi', or: 'Odia',
};

function normalizeLangCode(lang) {
  return String(lang || '').trim().toLowerCase().split('-', 1)[0];
}

function formatLangLabel(lang) {
  const normalized = normalizeLangCode(lang);
  return LANG_LABELS[normalized] || lang || 'Unknown';
}

function extractionTone(quality) {
  const normalized = String(quality || '').toLowerCase();
  if (normalized === 'high') return { bg: 'rgba(34,197,94,0.12)', color: '#22c55e', label: 'High trust extract' };
  if (normalized === 'medium') return { bg: 'rgba(245,158,11,0.12)', color: '#f59e0b', label: 'Needs light review' };
  if (normalized === 'low') return { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', label: 'Review carefully' };
  return { bg: 'var(--bg-elevated)', color: 'var(--text-muted)', label: 'Processing' };
}

const FILE_TYPE_ICON = (type) => {
  const t = (type || '').toLowerCase();
  if (['jpg', 'jpeg', 'png', 'tiff', 'webp'].includes(t)) return <ImageIcon size={16} style={{ color: 'var(--accent-primary)' }} />;
  if (['docx', 'doc'].includes(t)) return <FileType2 size={16} style={{ color: '#3b82f6' }} />;
  return <FileText size={16} style={{ color: 'var(--accent-primary)' }} />;
};

// ── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)',
      borderRadius: '10px', padding: '16px 20px', flex: '1 1 160px', minWidth: '140px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '6px' }}>
        <span style={{ color: 'var(--accent-primary)' }}>{icon}</span>
        <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', textTransform: 'uppercase', letterSpacing: '0.07em' }}>{label}</span>
      </div>
      <div style={{ fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', lineHeight: 1 }}>{value ?? '—'}</div>
      {sub && <div style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', marginTop: '4px' }}>{sub}</div>}
    </div>
  );
}

// ── Progress bar ─────────────────────────────────────────────────────────────
function ProgressBar({ value }) {
  return (
    <div style={{ height: '4px', background: 'var(--bg-elevated)', borderRadius: '2px', overflow: 'hidden', width: '100%' }}>
      <div style={{
        height: '100%', width: `${value}%`,
        background: 'var(--accent-primary)',
        borderRadius: '2px', transition: 'width 0.4s ease',
      }} />
    </div>
  );
}

function KnowledgeFileSkeleton() {
  return (
    <div className="animate-pulse space-y-3 py-4">
      {[0, 1, 2, 3].map((item) => (
        <div
          key={`knowledge-file-skeleton-${item}`}
          className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white/70 dark:bg-gray-900/40 p-4"
        >
          <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
          <div className="mt-3 h-4 bg-gray-200 dark:bg-gray-700 rounded w-5/6" />
        </div>
      ))}
    </div>
  );
}

// ── File detail slide panel ───────────────────────────────────────────────────
function FileDetailPanel({ file: initialFile, onClose, onFileUpdated, onPreview }) {
  const { getAuthHeaders, user } = useAuth();

  // Local copy so edits are reflected immediately without closing the panel
  const [file, setFile] = useState(initialFile);

  // Summary editing
  const [summary, setSummary] = useState(initialFile.summary || '');
  const [summaryDirty, setSummaryDirty] = useState(false);
  const [savingSummary, setSavingSummary] = useState(false);

  // User notes
  const [notes, setNotes] = useState(initialFile.userNotes || '');
  const [notesDirty, setNotesDirty] = useState(false);
  const [savingNotes, setSavingNotes] = useState(false);

  // Q&A
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [asking, setAsking] = useState(false);

  // Keep local file in sync if parent refreshes it
  useEffect(() => {
    setFile(initialFile);
    // Don't overwrite user edits that haven't been saved yet
    if (!summaryDirty) setSummary(initialFile.summary || '');
    if (!notesDirty) setNotes(initialFile.userNotes || '');
  }, [initialFile]); // eslint-disable-line

  useEffect(() => {
    let cancelled = false;

    const fetchLatestFile = async () => {
      try {
        const resp = await fetch(`${API}/knowledge/files/${initialFile.file_id}`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!resp.ok) return;
        const freshFile = await resp.json();
        if (cancelled) return;
        setFile(freshFile);
        if (!summaryDirty) setSummary(freshFile.summary || '');
        if (!notesDirty) setNotes(freshFile.userNotes || '');
        onFileUpdated?.(freshFile);
      } catch {}
    };

    if (initialFile?.file_id) fetchLatestFile();
    return () => { cancelled = true; };
  }, [initialFile?.file_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveField = async (field, value, setDirty, setSaving) => {
    setSaving(true);
    try {
      const resp = await fetch(`${API}/knowledge/files/${file.file_id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ [field]: value }),
      });
      if (resp.ok) {
        setDirty(false);
        const updated = { ...file, [field]: value };
        if (field === 'summary') updated.summaryEditedByUser = true;
        setFile(updated);
        onFileUpdated?.(updated);
        toast.success('Saved');
      } else {
        toast.error('Save failed');
      }
    } catch { toast.error('Save failed'); }
    finally { setSaving(false); }
  };

  const askQuestion = async () => {
    if (!question.trim()) return;
    setAsking(true);
    setAnswer('');
    try {
      const resp = await fetch(`${API}/knowledge/ask`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include',
        body: JSON.stringify({ question, matter_id: file.matterId || undefined }),
      });
      if (resp.ok) {
        const data = await resp.json();
        setAnswer(data.answer);
      }
    } catch { toast.error('Failed to get answer'); }
    finally { setAsking(false); }
  };

  const typeStyle = DOC_TYPE_STYLES[file.documentType] || DOC_TYPE_STYLES.OTHER;
  const preferredDisplayLanguage = normalizeDisplayLanguage(user?.language);
  const defaultRendering = chooseVisibleSourceText(file, preferredDisplayLanguage);
  const strategicInsights = file?.strategicInsights && typeof file.strategicInsights === 'object'
    ? file.strategicInsights
    : {};

  // All languages detected — deduplicated, primary first
  const allLangs = Array.from(new Set([
    ...(file.language ? [file.language] : []),
    ...(file.languagesDetected || []),
  ]));

  const sectionLabel = (text) => (
    <p style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', margin: '0 0 8px' }}>
      {text}
    </p>
  );

  return (
    <div style={{
      position: 'fixed', top: 0, right: 0, width: '440px', height: '100vh',
      background: 'var(--bg-surface)', borderLeft: '1px solid var(--border-subtle)',
      display: 'flex', flexDirection: 'column', zIndex: 50,
      boxShadow: '-8px 0 32px rgba(0,0,0,0.3)',
      animation: 'slideInRight 0.2s ease',
    }}>

      {/* ── Header ─────────────────────────────────────────────────── */}
      <div style={{ padding: '18px 20px 14px', borderBottom: '1px solid var(--border-subtle)', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px' }}>
          <div style={{ minWidth: 0, flex: 1 }}>
            {/* Doc-type badge */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '8px', flexWrap: 'wrap' }}>
              <span style={{ fontSize: '10px', padding: '2px 7px', borderRadius: '4px', fontFamily: 'var(--font-ui)', background: typeStyle.bg, color: typeStyle.color, fontWeight: 600 }}>
                {(file.documentType || 'OTHER').replace('_', ' ')}
              </span>
              {file.extractionMethod && (
                <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '3px', background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontFamily: "'JetBrains Mono', monospace", border: '1px solid var(--border-subtle)', textTransform: 'uppercase' }}>
                  {file.extractionMethod}
                </span>
              )}
            </div>

            <p style={{ fontSize: '14px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', margin: '0 0 6px', wordBreak: 'break-all', lineHeight: 1.3 }}>
              {file.originalName}
            </p>

            {/* Stats row */}
            <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              {file.pageCount > 0 && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{file.pageCount} pages</span>}
              {file.wordCount > 0 && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{file.wordCount.toLocaleString()} words</span>}
              {file.chunkCount > 0 && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{file.chunkCount} chunks indexed</span>}
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexShrink: 0 }}>
            <button
              onClick={() => onPreview?.(file)}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', padding: '8px 12px',
                borderRadius: '8px', border: '1px solid var(--border-default)', background: 'var(--bg-elevated)',
                color: 'var(--text-secondary)', cursor: 'pointer', fontSize: '12px', fontFamily: 'var(--font-ui)',
              }}
              title="Preview this file"
            >
              <Eye size={13} />
              Preview
            </button>
            <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', flexShrink: 0, display: 'flex', padding: '4px', marginTop: '2px' }}>
              <X size={17} />
            </button>
          </div>
        </div>

        {/* ── Languages detected ──────────────────────────────────── */}
        {allLangs.length > 0 && (
          <div style={{ marginTop: '12px', padding: '10px 12px', background: 'var(--bg-elevated)', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '7px' }}>
              <Languages size={12} style={{ color: 'var(--accent-primary)' }} />
              <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                {allLangs.length === 1 ? 'Language detected' : 'Languages detected'}
              </span>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {allLangs.map((lang, i) => (
                <span key={lang} style={{
                  fontSize: '11px', padding: '3px 9px', borderRadius: '5px',
                  fontFamily: 'var(--font-ui)', fontWeight: i === 0 ? 600 : 400,
                  background: i === 0 ? 'rgba(46,204,90,0.12)' : 'var(--bg-surface)',
                  color: i === 0 ? 'var(--accent-primary)' : 'var(--text-secondary)',
                  border: i === 0 ? '1px solid rgba(46,204,90,0.25)' : '1px solid var(--border-subtle)',
                }}>
                  {formatLangLabel(lang)}
                  {i === 0 && <span style={{ fontSize: '9px', opacity: 0.7, marginLeft: '4px' }}>primary</span>}
                </span>
              ))}
            </div>
            {file.translatedToEnglish && (
              <p style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', margin: '6px 0 0' }}>
                Translated to English for indexing · original language preserved in search results
              </p>
            )}
          </div>
        )}
      </div>

      {/* ── Body ───────────────────────────────────────────────────── */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '18px 20px' }}>

        <div style={{ marginBottom: '20px', padding: '14px', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap', marginBottom: '10px' }}>
            <span style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
              Trust & provenance
            </span>
            <SourceTrustBadge label={file.userVisibleTrustLabel || file.trustLabel || 'Matter File'} compact />
            <span style={{
              fontSize: '10px',
              padding: '3px 8px',
              borderRadius: '999px',
              background: extractionTone(file.extractionQuality).bg,
              color: extractionTone(file.extractionQuality).color,
              fontFamily: 'var(--font-ui)',
              fontWeight: 700,
            }}>
              {extractionTone(file.extractionQuality).label}
            </span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px' }}>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>Original preserved:</strong> Yes
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>English render:</strong> {file.englishText ? 'Available' : file.translatedToEnglish ? 'Added for search' : 'Not needed'}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>OCR confidence:</strong> {typeof file.ocrConfidence === 'number' ? `${Math.round(file.ocrConfidence * 100)}%` : 'Not used'}
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              <strong style={{ color: 'var(--text-primary)' }}>Language mix:</strong> {file.mixedLanguage ? 'Mixed-language file' : 'Single primary language'}
            </div>
          </div>
          {file.extractionWarnings?.length ? (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6 }}>
              {file.extractionWarnings.slice(0, 3).map((warning, index) => (
                <div key={`${warning}-${index}`}>• {warning}</div>
              ))}
            </div>
          ) : null}
        </div>

        {(strategicInsights.roleLabel
          || strategicInsights.smartTags?.length
          || strategicInsights.riskFlags?.length
          || strategicInsights.suggestedActions?.length
          || strategicInsights.proofPoints?.length
          || strategicInsights.opponentSignals?.length
          || strategicInsights.missingReferences?.length) && (
          <div style={{ marginBottom: '20px' }}>
            {sectionLabel('Vakalat Base strategy')}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ padding: '14px', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                  <div>
                    <p style={{ margin: 0, fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
                      {strategicInsights.roleLabel || 'Strategic profile'}
                    </p>
                    <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.6, fontFamily: 'var(--font-ui)' }}>
                      {strategicInsights.role === 'opponent_pleading'
                        ? 'Treat this file as an adversarial document. Focus on rebuttal points, unsupported assertions, and proof gaps.'
                        : strategicInsights.role === 'court_signal'
                        ? 'Treat this file as a controlling court signal. Anchor strategy to what the order or judgment actually says.'
                        : 'Use this file as part of the matter record, but keep proof, chronology, and follow-up actions tightly linked.'}
                    </p>
                  </div>
                  {strategicInsights.generatedAt && (
                    <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                      Refreshed {new Date(strategicInsights.generatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </span>
                  )}
                </div>
                {strategicInsights.smartTags?.length > 0 && (
                  <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '10px' }}>
                    {strategicInsights.smartTags.map((tag) => (
                      <span key={tag} style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: 'var(--bg-surface)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-ui)' }}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>

              {[
                { key: 'riskFlags', label: 'Risk flags', color: '#dc2626', items: strategicInsights.riskFlags || [] },
                { key: 'missingReferences', label: 'Missing references', color: '#b45309', items: strategicInsights.missingReferences || [] },
                { key: 'suggestedActions', label: 'Suggested actions', color: '#2563eb', items: strategicInsights.suggestedActions || [] },
                { key: 'proofPoints', label: 'Proof points', color: 'var(--accent-primary)', items: strategicInsights.proofPoints || [] },
                { key: 'opponentSignals', label: 'Opponent signals', color: '#7c3aed', items: strategicInsights.opponentSignals || [] },
              ].map((group) => (
                group.items?.length ? (
                  <div key={group.key} style={{ padding: '14px', borderRadius: '10px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                    <p style={{ margin: 0, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: group.color, fontWeight: 700, fontFamily: 'var(--font-ui)' }}>
                      {group.label}
                    </p>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '10px' }}>
                      {group.items.slice(0, 5).map((item, index) => (
                        <div key={item.id || `${group.key}-${index}`} style={{ padding: '10px 12px', borderRadius: '8px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', flexWrap: 'wrap' }}>
                            <p style={{ margin: 0, fontSize: '12px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
                              {item.title || item.description}
                            </p>
                            {item.severity && (
                              <span style={{
                                fontSize: '10px',
                                padding: '3px 8px',
                                borderRadius: '999px',
                                background: item.severity === 'high' ? 'rgba(239,68,68,0.12)' : 'rgba(245,158,11,0.12)',
                                color: item.severity === 'high' ? '#dc2626' : '#b45309',
                                fontFamily: 'var(--font-ui)',
                                fontWeight: 700,
                                textTransform: 'uppercase',
                              }}>
                                {item.severity}
                              </span>
                            )}
                          </div>
                          {item.description && (
                            <p style={{ margin: '6px 0 0', fontSize: '12px', color: 'var(--text-secondary)', lineHeight: 1.55, fontFamily: 'var(--font-ui)' }}>
                              {item.description}
                            </p>
                          )}
                          {item.citation?.quote && (
                            <p style={{ margin: '8px 0 0', fontSize: '11px', color: 'var(--text-muted)', lineHeight: 1.55, fontFamily: 'var(--font-ui)' }}>
                              {item.citation.label ? `${item.citation.label}: ` : ''}{item.citation.quote}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null
              ))}
            </div>
          </div>
        )}

        {(file.rawText || file.englishText) ? (
          <div style={{ marginBottom: '20px' }}>
            {sectionLabel('Rendering')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '12px' }}>
              <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'var(--font-ui)' }}>
                  Default rendering
                </p>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '12px', lineHeight: 1.75, color: 'var(--text-primary)', fontFamily: defaultRendering.language === 'en' ? 'var(--font-ui)' : "'Noto Sans', 'Noto Sans Devanagari', 'Noto Sans Bengali', 'Noto Sans Tamil', 'Noto Sans Telugu', sans-serif", maxHeight: '180px', overflowY: 'auto' }}>
                  {(defaultRendering.text || 'A clean default rendering is not ready yet.').slice(0, 2400)}
                </pre>
              </div>
              <div style={{ padding: '12px', borderRadius: '8px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)' }}>
                <p style={{ margin: '0 0 8px', fontSize: '11px', fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: 'var(--font-ui)' }}>
                  Original extract
                </p>
                <pre style={{ margin: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere', fontSize: '12px', lineHeight: 1.75, color: 'var(--text-primary)', fontFamily: "'Noto Sans', 'Noto Sans Devanagari', 'Noto Sans Bengali', 'Noto Sans Tamil', 'Noto Sans Telugu', sans-serif", maxHeight: '180px', overflowY: 'auto' }}>
                  {(file.rawText || 'Original extract is not ready yet.').slice(0, 2400)}
                </pre>
              </div>
            </div>
          </div>
        ) : null}

        {/* ── Editable summary ──────────────────────────────────────── */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {sectionLabel('Summary')}
              {file.summaryEditedByUser
                ? <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontFamily: 'var(--font-ui)', marginBottom: '8px', fontWeight: 600 }}>Edited by you</span>
                : <span style={{ fontSize: '9px', padding: '1px 5px', borderRadius: '3px', background: 'rgba(46,204,90,0.08)', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '3px' }}><Sparkles size={8} />AI generated</span>
              }
            </div>
            {summaryDirty && (
              <button
                onClick={() => saveField('summary', summary, setSummaryDirty, setSavingSummary)}
                disabled={savingSummary}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '5px', border: 'none', background: 'var(--accent-primary)', color: 'var(--accent-foreground)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}
              >
                {savingSummary ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                Save
              </button>
            )}
          </div>
          <textarea
            value={summary}
            onChange={e => { setSummary(e.target.value); setSummaryDirty(true); }}
            onBlur={() => { if (summaryDirty) saveField('summary', summary, setSummaryDirty, setSavingSummary); }}
            placeholder="No summary yet — add one to help the AI understand this document better."
            rows={5}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: '7px', resize: 'vertical',
              background: 'var(--bg-elevated)', border: `1px solid ${summaryDirty ? 'var(--accent-primary)' : 'var(--border-default)'}`,
              color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.65,
              fontFamily: 'var(--font-ui)', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
          />
          <p style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', margin: '4px 0 0' }}>
            Edits are saved automatically on blur · the AI uses this summary when referencing this file
          </p>
        </div>

        {/* ── Key parties ───────────────────────────────────────────── */}
        {file.keyEntities?.length > 0 && (
          <div style={{ marginBottom: '20px' }}>
            {sectionLabel('Key parties')}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
              {file.keyEntities.map((e, i) => (
                <span key={i} style={{ fontSize: '11px', padding: '3px 8px', borderRadius: '4px', background: 'var(--accent-glow)', color: 'var(--accent-primary)', border: '1px solid rgba(46,204,90,0.2)', fontFamily: 'var(--font-ui)' }}>{e}</span>
              ))}
            </div>
          </div>
        )}

        {/* ── User notes ────────────────────────────────────────────── */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <StickyNote size={11} style={{ color: 'var(--text-muted)', marginBottom: '0' }} />
              {sectionLabel('Your notes')}
            </div>
            {notesDirty && (
              <button
                onClick={() => saveField('userNotes', notes, setNotesDirty, setSavingNotes)}
                disabled={savingNotes}
                style={{ display: 'flex', alignItems: 'center', gap: '4px', padding: '3px 10px', borderRadius: '5px', border: 'none', background: 'var(--accent-primary)', color: 'var(--accent-foreground)', fontSize: '11px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}
              >
                {savingNotes ? <Loader2 size={10} className="animate-spin" /> : <Save size={10} />}
                Save
              </button>
            )}
          </div>
          <textarea
            value={notes}
            onChange={e => { setNotes(e.target.value); setNotesDirty(true); }}
            onBlur={() => { if (notesDirty) saveField('userNotes', notes, setNotesDirty, setSavingNotes); }}
            placeholder="Add your own notes, context, or corrections about this document…"
            rows={3}
            style={{
              width: '100%', padding: '10px 12px', borderRadius: '7px', resize: 'vertical',
              background: 'var(--bg-elevated)', border: `1px solid ${notesDirty ? 'var(--accent-primary)' : 'var(--border-default)'}`,
              color: 'var(--text-primary)', fontSize: '13px', lineHeight: 1.65,
              fontFamily: 'var(--font-ui)', outline: 'none', boxSizing: 'border-box',
              transition: 'border-color 0.15s',
            }}
          />
        </div>

        {/* ── Ask about this file ───────────────────────────────────── */}
        <div style={{ background: 'var(--bg-elevated)', borderRadius: '8px', padding: '14px', border: '1px solid var(--border-subtle)' }}>
          <p style={{ fontSize: '11px', fontWeight: 700, color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.07em' }}>Ask about this file</p>
          <div style={{ display: 'flex', gap: '8px' }}>
            <input
              value={question}
              onChange={e => setQuestion(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && askQuestion()}
              placeholder="What is the charge in this FIR?"
              style={{ flex: 1, padding: '8px 10px', borderRadius: '6px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-ui)', outline: 'none' }}
            />
            <button onClick={askQuestion} disabled={asking || !question.trim()}
              style={{ padding: '8px 14px', borderRadius: '6px', border: 'none', background: 'var(--accent-primary)', color: 'var(--accent-foreground)', fontSize: '12px', fontWeight: 600, cursor: asking ? 'wait' : 'pointer', fontFamily: 'var(--font-ui)', flexShrink: 0, opacity: (!question.trim() || asking) ? 0.6 : 1 }}
            >
              {asking ? <Loader2 size={12} className="animate-spin" /> : 'Ask'}
            </button>
          </div>
          {answer && (
            <div style={{ marginTop: '10px', fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.6, padding: '10px', background: 'var(--bg-surface)', borderRadius: '6px', border: '1px solid var(--border-subtle)' }}>
              {answer}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Individual file card ──────────────────────────────────────────────────────
function FileCard({ file, onDelete, onRetry, onView, onPreview, displayLanguage = 'en' }) {
  const [hovered, setHovered] = useState(false);
  const typeStyle = DOC_TYPE_STYLES[file.documentType] || DOC_TYPE_STYLES.OTHER;
  const date = file.createdAt
    ? new Date(file.createdAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
    : '';
  const visibleText = chooseVisibleSourceText(file, displayLanguage);
  const languageLabel = buildSourceLanguageLabel(file, displayLanguage);

  const isProcessing = file.status === 'processing';
  const isReady = file.status === 'ready';
  const isFailed = file.status === 'failed';

  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: 'var(--bg-surface)',
        border: `1px solid ${hovered ? 'var(--border-default)' : 'var(--border-subtle)'}`,
        borderRadius: '10px', overflow: 'hidden', transition: 'border-color 0.15s',
      }}
    >
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        {/* File type icon */}
        <div style={{
          width: '36px', height: '36px', borderRadius: '8px', flexShrink: 0,
          background: 'var(--accent-glow)', border: '1px solid rgba(46,204,90,0.2)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '2px',
        }}>
          {FILE_TYPE_ICON(file.fileType)}
        </div>

        {/* Content */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '300px' }}>
              {file.originalName}
            </span>
            {file.documentType && file.documentType !== 'OTHER' && (
              <span style={{ fontSize: '9px', padding: '1px 6px', borderRadius: '3px', fontFamily: 'var(--font-ui)', background: typeStyle.bg, color: typeStyle.color, fontWeight: 600, flexShrink: 0 }}>
                {file.documentType.replace('_', ' ')}
              </span>
            )}
          </div>

          {/* Summary */}
          {isReady && (file.summary || visibleText.text) && (
            <p style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.5, margin: '0 0 6px', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
              {file.summary || visibleText.text}
            </p>
          )}

          {/* Key entity chips */}
          {isReady && file.keyEntities?.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '6px' }}>
              {file.keyEntities.slice(0, 3).map((e, i) => (
                <span key={i} style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'var(--bg-elevated)', color: 'var(--text-muted)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-ui)' }}>{e}</span>
              ))}
            </div>
          )}

          {/* Metadata row */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
            <SourceTrustBadge label={file.userVisibleTrustLabel || file.trustLabel || 'Matter File'} compact />
            {/* Show all detected languages; primary is green, others are muted */}
            {Array.from(new Set([
              ...(file.language ? [file.language] : []),
              ...(file.languagesDetected || []),
            ])).map((lang, i) => (
              <span key={lang} style={{
                fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                fontFamily: 'var(--font-ui)',
                background: i === 0 ? 'rgba(46,204,90,0.08)' : 'var(--bg-elevated)',
                color: i === 0 ? 'var(--accent-primary)' : 'var(--text-muted)',
              }}>
                {formatLangLabel(lang)}
              </span>
            ))}
            {file.extractionQuality ? (
              <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: extractionTone(file.extractionQuality).bg, color: extractionTone(file.extractionQuality).color, fontFamily: 'var(--font-ui)', fontWeight: 700 }}>
                {extractionTone(file.extractionQuality).label}
              </span>
            ) : null}
            {file.translatedToEnglish || file.englishText ? (
              <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontFamily: 'var(--font-ui)' }}>
                {languageLabel || 'Original + English'}
              </span>
            ) : null}
            {file.mixedLanguage ? (
              <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(168,85,247,0.12)', color: '#a855f7', fontFamily: 'var(--font-ui)' }}>
                Mixed language
              </span>
            ) : null}
            {file.pageCount > 0 && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{file.pageCount} pages</span>}
            {file.wordCount > 0 && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{file.wordCount.toLocaleString()} words</span>}
            {date && <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{date}</span>}
            {file.matterId && <span style={{ fontSize: '10px', padding: '1px 6px', borderRadius: '3px', background: 'rgba(59,130,246,0.1)', color: '#3b82f6', fontFamily: 'var(--font-ui)' }}>{file.matterId}</span>}
          </div>

          {/* Processing progress */}
          {isProcessing && (
            <div style={{ marginTop: '8px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '4px' }}>
                <span style={{ fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', display: 'flex', alignItems: 'center', gap: '5px' }}>
                  <Loader2 size={10} className="animate-spin" style={{ display: 'inline', marginRight: '4px' }} />
                  {file.processingStep || 'Processing…'}
                </span>
                <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>{file.progress || 0}%</span>
              </div>
              <ProgressBar value={file.progress || 0} />
            </div>
          )}

          {/* Failed state */}
          {isFailed && (
            <div style={{ marginTop: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
              <AlertCircle size={12} style={{ color: '#ef4444', flexShrink: 0 }} />
              <span style={{ fontSize: '11px', color: '#ef4444', fontFamily: 'var(--font-ui)' }}>
                {file.errorMessage ? file.errorMessage.slice(0, 80) : 'Processing failed'}
              </span>
            </div>
          )}
        </div>

        {/* Right side status + actions */}
        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '6px' }}>
          {isReady && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <CheckCircle2 size={13} style={{ color: 'var(--accent-primary)' }} />
              <span style={{ fontSize: '10px', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>Ready</span>
            </div>
          )}
          {isProcessing && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <Loader2 size={12} className="animate-spin" style={{ color: 'var(--text-muted)' }} />
              <span style={{ fontSize: '10px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>Processing</span>
            </div>
          )}
          {isFailed && (
            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
              <AlertCircle size={12} style={{ color: '#ef4444' }} />
              <span style={{ fontSize: '10px', color: '#ef4444', fontFamily: 'var(--font-ui)' }}>Failed</span>
            </div>
          )}

          {/* Actions — visible on hover */}
          <div style={{ display: 'flex', gap: '2px', opacity: hovered ? 1 : 0, transition: 'opacity 0.15s' }}>
            <button onClick={() => onPreview(file)} title="Preview file"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: 'var(--text-muted)', display: 'flex', borderRadius: '4px' }}
              onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-primary)'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            >
              <Eye size={13} />
            </button>
            {isFailed && (
              <button onClick={() => onRetry(file.file_id)} title="Retry processing"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: 'var(--text-muted)', display: 'flex', borderRadius: '4px' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <RotateCcw size={13} />
              </button>
            )}
            {isReady && (
              <button onClick={() => onView(file)} title="Edit summary and notes"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: 'var(--text-muted)', display: 'flex', borderRadius: '4px' }}
                onMouseEnter={e => e.currentTarget.style.color = 'var(--accent-primary)'}
                onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
              >
                <Pencil size={13} />
              </button>
            )}
            <button onClick={() => onDelete(file.file_id, file.originalName)} title="Delete"
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '5px', color: 'var(--text-muted)', display: 'flex', borderRadius: '4px' }}
              onMouseEnter={e => e.currentTarget.style.color = '#ef4444'}
              onMouseLeave={e => e.currentTarget.style.color = 'var(--text-muted)'}
            >
              <Trash2 size={13} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function KnowledgeBasePage() {
  const { getAuthHeaders, user } = useAuth();
  const [files, setFiles] = useState([]);
  const [stats, setStats] = useState(null);
  const [registry, setRegistry] = useState(null);
  const [matters, setMatters] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterLang, setFilterLang] = useState('');
  const [filterDocType, setFilterDocType] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [detailFile, setDetailFile] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewTitle, setPreviewTitle] = useState('');
  const [showSourceLibrary, setShowSourceLibrary] = useState(false);
  const [selectedMatterId, setSelectedMatterId] = useState('');
  const fileInputRef = useRef(null);
  const wsRef = useRef(null);
  const pollRef = useRef(null);

  const openPreview = useCallback((file) => {
    if (!file?.file_id) return;
    setPreviewUrl(getKnowledgePreviewUrl(file.file_id));
    setPreviewTitle(file.originalName || 'Knowledge file');
  }, []);

  // ── Load files & stats ────────────────────────────────────────────────────
  const loadFiles = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/knowledge/files?limit=200`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        setFiles(data.files || []);
      }
    } catch { toast.error('Failed to load knowledge base'); }
    finally { setLoading(false); }
  }, [getAuthHeaders]);

  const loadStats = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/knowledge/stats`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) setStats(await resp.json());
    } catch {}
  }, [getAuthHeaders]);

  const loadRegistry = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/verified-corpus/registry`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) setRegistry(await resp.json());
    } catch {}
  }, [getAuthHeaders]);

  const loadMatters = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/matters`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) {
        const data = await resp.json();
        setMatters(Array.isArray(data) ? data : (data?.matters || []));
      }
    } catch {}
  }, [getAuthHeaders]);

  const loadOverview = useCallback(async () => {
    try {
      const resp = await fetch(`${API}/knowledge/vakalat-base/overview`, {
        headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) setOverview(await resp.json());
    } catch {}
  }, [getAuthHeaders]);

  useEffect(() => {
    document.title = 'Knowledge Base — Advoq';
  }, []);

  useEffect(() => {
    setLoading(true);
    loadFiles();
    loadStats();
    loadRegistry();
    loadMatters();
    loadOverview();
  }, [loadFiles, loadRegistry, loadStats, loadMatters, loadOverview]);

  useEffect(() => {
    const matterOptions = matters.filter(matter => matter?.matter_id);
    if (!matterOptions.length) {
      if (selectedMatterId) setSelectedMatterId('');
      return;
    }
    const stillValid = matterOptions.some(matter => matter.matter_id === selectedMatterId);
    if (!selectedMatterId || !stillValid) {
      setSelectedMatterId(matterOptions[0].matter_id);
    }
  }, [matters, selectedMatterId]);

  // ── WebSocket for real-time status ────────────────────────────────────────
  useEffect(() => {
    if (!user?.user_id) return;
    const token = localStorage.getItem('session_token');
    const wsUrl = `${WS_URL}/ws/knowledge/${user.user_id}${token ? `?token=${token}` : ''}`;
    let ws;
    try {
      ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onmessage = (e) => {
        try {
          const msg = JSON.parse(e.data);
          if (msg.fileId) {
            setFiles(prev => prev.map(f =>
              f.file_id === msg.fileId
                ? { ...f, status: msg.status, processingStep: msg.processingStep, progress: msg.progress }
                : f
            ));
            if (msg.status === 'ready') {
              loadFiles();
              loadStats();
              loadMatters();
              loadOverview();
            }
          }
        } catch {}
      };
    } catch {}
    return () => { try { ws?.close(); } catch {} };
  }, [user?.user_id, loadFiles, loadStats, loadMatters, loadOverview]);

  // ── Poll every 4s when files are processing ───────────────────────────────
  useEffect(() => {
    const hasProcessing = files.some(f => f.status === 'processing');
    if (hasProcessing) {
      pollRef.current = setInterval(() => {
        loadFiles();
        loadStats();
      }, 4000);
    } else {
      clearInterval(pollRef.current);
    }
    return () => clearInterval(pollRef.current);
  }, [files, loadFiles, loadStats]);

  // ── Upload ────────────────────────────────────────────────────────────────
  const uploadFiles = async (fileList) => {
    if (!fileList?.length) return;
    const allowed = new Set(['.pdf', '.docx', '.doc', '.jpg', '.jpeg', '.png', '.tiff', '.webp', '.txt']);
    const valid = Array.from(fileList).filter(f => {
      const ext = '.' + f.name.split('.').pop().toLowerCase();
      if (!allowed.has(ext)) { toast.error(`${f.name}: unsupported type`); return false; }
      if (f.size > 50 * 1024 * 1024) { toast.error(`${f.name}: exceeds 50 MB`); return false; }
      return true;
    });
    if (!valid.length) return;

    const token = localStorage.getItem('session_token');
    const BATCH_SIZE = 200; // backend accepts up to 200 per request

    // For large uploads use the batch endpoint in chunks of BATCH_SIZE
    if (valid.length > 1) {
      const batchId = `batch_${Date.now()}`;
      setUploading(prev => [...prev, { id: batchId, name: `${valid.length} files`, progress: 0, isBatch: true }]);
      let totalUploaded = 0;

      for (let i = 0; i < valid.length; i += BATCH_SIZE) {
        const chunk = valid.slice(i, i + BATCH_SIZE);
        const formData = new FormData();
        chunk.forEach(f => formData.append('files', f));

        try {
          const resp = await fetch(`${API}/knowledge/upload/batch`, {
            method: 'POST',
            headers: token ? { Authorization: `Bearer ${token}` } : {},
            credentials: 'include',
            body: formData,
          });
          if (resp.ok) {
            const result = await resp.json();
            totalUploaded += result.succeeded || 0;
            // Add placeholder cards for each uploaded file
            const newCards = (result.uploaded || []).map(r => ({
              file_id: r.fileId,
              originalName: r.fileName,
              fileType: r.fileName?.split('.').pop()?.toLowerCase() || '',
              status: 'processing',
              processingStep: 'Queued',
              progress: 0,
              createdAt: new Date().toISOString(),
            }));
            if (newCards.length) setFiles(prev => [...newCards, ...prev]);
            if (result.errors?.length) {
              result.errors.slice(0, 5).forEach(e => toast.error(`${e.filename}: ${e.error}`));
              if (result.errors.length > 5) toast.error(`…and ${result.errors.length - 5} more errors`);
            }
          } else {
            const err = await resp.json().catch(() => ({}));
            toast.error(err.detail || `Batch upload failed (files ${i + 1}–${i + chunk.length})`);
          }
        } catch {
          toast.error(`Batch upload error (files ${i + 1}–${i + chunk.length})`);
        }
      }

      setUploading(prev => prev.filter(u => u.id !== batchId));
      if (totalUploaded > 0) {
        toast.success(`${totalUploaded} of ${valid.length} files queued for processing`);
        loadStats();
      }
      return;
    }

    // Single file — use individual endpoint for immediate preview
    const file = valid[0];
    const tempId = `tmp_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    setUploading(prev => [...prev, { id: tempId, name: file.name, progress: 0 }]);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const resp = await fetch(`${API}/knowledge/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
        body: formData,
      });
      if (resp.ok) {
        const result = await resp.json();
        toast.success(`${file.name} uploaded — processing…`);
        openPreview({ file_id: result.fileId, originalName: result.fileName || file.name });
        setFiles(prev => [{
          file_id: result.fileId,
          originalName: result.fileName || file.name,
          fileType: file.name.split('.').pop().toLowerCase(),
          status: 'processing',
          processingStep: 'Queued',
          progress: 0,
          createdAt: new Date().toISOString(),
        }, ...prev]);
        loadStats();
      } else {
        const err = await resp.json().catch(() => ({}));
        toast.error(`${file.name}: ${err.detail || 'Upload failed'}`);
      }
    } catch {
      toast.error(`${file.name}: upload error`);
    }

    setUploading(prev => prev.filter(u => u.id !== tempId));
  };

  // ── Delete ────────────────────────────────────────────────────────────────
  const deleteFile = async (fileId, filename) => {
    try {
      const resp = await fetch(`${API}/knowledge/files/${fileId}`, {
        method: 'DELETE', headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) {
        setFiles(prev => prev.filter(f => f.file_id !== fileId));
        toast.success(`"${filename}" moved to trash`);
        loadStats();
        if (detailFile?.file_id === fileId) setDetailFile(null);
      }
    } catch { toast.error('Failed to move file to trash'); }
    finally { setDeleteConfirm(null); }
  };

  // ── Retry ─────────────────────────────────────────────────────────────────
  const retryFile = async (fileId) => {
    try {
      const resp = await fetch(`${API}/knowledge/files/${fileId}/retry`, {
        method: 'POST', headers: getAuthHeaders(), credentials: 'include',
      });
      if (resp.ok) {
        setFiles(prev => prev.map(f => f.file_id === fileId ? { ...f, status: 'processing', progress: 0, processingStep: 'Queued' } : f));
        toast.success('Retrying processing…');
      }
    } catch { toast.error('Retry failed'); }
  };

  // ── Filter + sort ─────────────────────────────────────────────────────────
  const filtered = files
    .filter(f => {
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        if (
          !f.originalName?.toLowerCase().includes(q) &&
          !f.summary?.toLowerCase().includes(q) &&
          !f.documentType?.toLowerCase().includes(q)
        ) return false;
      }
      if (filterType !== 'all') {
        const ft = (f.fileType || '').toLowerCase();
        if (filterType === 'pdf' && ft !== 'pdf') return false;
        if (filterType === 'image' && !['jpg', 'jpeg', 'png', 'tiff', 'webp'].includes(ft)) return false;
        if (filterType === 'word' && !['docx', 'doc'].includes(ft)) return false;
      }
      if (filterLang && f.language !== filterLang) return false;
      if (filterDocType && f.documentType !== filterDocType) return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === 'oldest') return new Date(a.createdAt) - new Date(b.createdAt);
      if (sortBy === 'type') return (a.documentType || '').localeCompare(b.documentType || '');
      return new Date(b.createdAt) - new Date(a.createdAt); // newest
    });

  const allLanguages = [...new Set(files.map(f => f.language).filter(Boolean))];
  const allDocTypes = [...new Set(files.map(f => f.documentType).filter(Boolean))];
  const activeMatter = matters.find(matter => matter.matter_id === selectedMatterId) || null;
  const activeMatterFileCount = activeMatter?.knowledge_file_count
    || files.filter(file => file.matterId === selectedMatterId).length;

  const selectStyle = {
    background: 'var(--bg-surface)', border: '1px solid var(--border-default)',
    borderRadius: '6px', padding: '5px 8px', color: 'var(--text-secondary)',
    fontSize: '12px', fontFamily: 'var(--font-ui)', cursor: 'pointer', outline: 'none',
  };

  return (
    <div style={{ display: 'contents' }}>
      <main className="app-main" style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden', position: 'relative', height: '100%', minHeight: 0 }}>

        {/* Header */}
        <div style={{ padding: '20px 32px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--bg-surface)', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '16px' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                <Database size={20} style={{ color: 'var(--accent-primary)' }} />
                <h1 style={{ fontFamily: 'var(--font-display)', fontSize: '22px', fontWeight: 700, color: 'var(--text-primary)', margin: 0 }}>
                  Vakalat Base
                </h1>
              </div>
              <p style={{ fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', margin: 0 }}>
                Litigation memory, proof-backed retrieval, and matter strategy for Indian lawyers in one workspace.
              </p>
            </div>
            <div style={{ display: 'flex', gap: '8px', flexShrink: 0 }}>
              <button onClick={() => { loadFiles(); loadStats(); loadMatters(); loadOverview(); }} style={{ display: 'flex', alignItems: 'center', gap: '5px', padding: '7px 14px', borderRadius: '7px', border: '1px solid var(--border-default)', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', fontSize: '12px', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                <RefreshCw size={12} /> Refresh
              </button>
              <button onClick={() => fileInputRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: '6px', padding: '7px 16px', borderRadius: '7px', border: 'none', background: 'var(--accent-primary)', color: 'var(--accent-foreground)', fontSize: '12px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>
                <Upload size={12} /> Add Sources
              </button>
            </div>
          </div>
        </div>

        <div style={{ flex: 1, overflowY: showSourceLibrary ? 'auto' : 'hidden', padding: '20px 24px' }}>

          {(stats || loading) && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <StatCard icon={<Layers size={14} />} label="Files" value={loading ? '…' : (stats?.totalFiles ?? 0)} />
              <StatCard icon={<BookOpen size={14} />} label="Pages" value={loading ? '…' : (stats?.totalPages ?? 0).toLocaleString()} />
              <StatCard icon={<Globe size={14} />} label="Languages" value={loading ? '…' : (stats?.languagesDetected?.length ?? 0)} />
              <StatCard icon={<HardDrive size={14} />} label="Storage" value={loading ? '…' : `${stats?.storageUsedMB ?? 0} MB`} />
            </div>
          )}

          {overview?.portfolioMetrics && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap' }}>
              <StatCard icon={<Database size={14} />} label="Matters" value={overview.portfolioMetrics.matterCount ?? 0} sub="Tracked in Vakalat Base" />
              <StatCard icon={<AlertCircle size={14} />} label="Urgent Risks" value={overview.portfolioMetrics.urgentRiskCount ?? 0} sub="High-severity matter signals" />
              <StatCard icon={<FileText size={14} />} label="Missing Links" value={overview.portfolioMetrics.missingLinkCount ?? 0} sub="Referenced records to verify" />
              <StatCard icon={<CheckCircle2 size={14} />} label="Hearing Ready" value={overview.portfolioMetrics.hearingReadyMatterCount ?? 0} sub="Matters with stronger posture" />
            </div>
          )}

          {(overview?.spotlightMatters?.length > 0 || selectedMatterId) && (
            <div style={{ marginBottom: '18px', padding: '18px', borderRadius: '16px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700 }}>
                    Vakalat Base cockpit
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                    Turn documents into hearing posture, risk radar, proof packs, and next moves.
                  </p>
                </div>
                {matters.length > 0 && (
                  <select value={selectedMatterId} onChange={e => setSelectedMatterId(e.target.value)} style={{ ...selectStyle, minWidth: '220px' }}>
                    {matters.filter(matter => matter?.matter_id).map((matter) => (
                      <option key={matter.matter_id} value={matter.matter_id}>
                        {matter.title || matter.name || matter.matter_id}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              {overview?.urgentActions?.length > 0 && (
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
                  {overview.urgentActions.slice(0, 4).map((action) => (
                    <span key={`${action.matterId}-${action.title}`} style={{ fontSize: '10px', padding: '5px 9px', borderRadius: '999px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-ui)' }}>
                      {(action.matterTitle || 'Matter')}: {action.title}
                    </span>
                  ))}
                </div>
              )}

              {selectedMatterId ? (
                <MatterIntelligenceCard matterId={selectedMatterId} fileCount={activeMatterFileCount} />
              ) : (
                <p style={{ margin: 0, fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                  Link a few sources to a matter to unlock the full Vakalat Base cockpit.
                </p>
              )}

              {overview?.spotlightMatters?.length > 0 && (
                <div style={{ marginTop: '14px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '10px' }}>
                  {overview.spotlightMatters.slice(0, 3).map((matter) => (
                    <button
                      key={matter.matterId}
                      type="button"
                      onClick={() => setSelectedMatterId(matter.matterId)}
                      style={{
                        textAlign: 'left',
                        padding: '12px 14px',
                        borderRadius: '12px',
                        border: `1px solid ${selectedMatterId === matter.matterId ? 'var(--accent-primary)' : 'var(--border-subtle)'}`,
                        background: selectedMatterId === matter.matterId ? 'var(--accent-glow)' : 'var(--bg-elevated)',
                        cursor: 'pointer',
                      }}
                    >
                      <p style={{ margin: 0, fontSize: '12px', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)' }}>
                        {matter.title}
                      </p>
                      <p style={{ margin: '6px 0 0', fontSize: '11px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', lineHeight: 1.5 }}>
                        {matter.matterPulse || `${matter.sourceCount || 0} sources in this matter.`}
                      </p>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginTop: '8px' }}>
                        <span style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: 'rgba(59,130,246,0.1)', color: '#2563eb', fontFamily: 'var(--font-ui)', fontWeight: 700 }}>
                          {matter.readinessLabel} · {matter.readinessScore}
                        </span>
                        {matter.urgentRiskCount ? (
                          <span style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: 'rgba(239,68,68,0.12)', color: '#dc2626', fontFamily: 'var(--font-ui)', fontWeight: 700 }}>
                            {matter.urgentRiskCount} urgent
                          </span>
                        ) : null}
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {registry?.metrics && (
            <div style={{ marginBottom: '18px', padding: '16px 18px', borderRadius: '14px', background: 'var(--bg-surface)', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', marginBottom: '10px' }}>
                <div>
                  <p style={{ margin: 0, fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--accent-primary)', fontFamily: 'var(--font-ui)', fontWeight: 700 }}>
                    Verified public corpus
                  </p>
                  <p style={{ margin: '6px 0 0', fontSize: '13px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)' }}>
                    {registry.metrics.source_family_count} official source families scaffolded for crawl-safe ingestion across courts, statutes, gazettes, and regulators.
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                  <SourceTrustBadge label="Verified Government Source" compact />
                  <span style={{ fontSize: '10px', padding: '4px 8px', borderRadius: '999px', background: 'var(--bg-elevated)', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)' }}>
                    {registry.metrics.adapter_ready_count} adapters ready
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                {(registry.metrics.material_types_supported || []).slice(0, 10).map((item) => (
                  <span key={item} style={{ fontSize: '10px', padding: '3px 8px', borderRadius: '999px', background: 'var(--bg-elevated)', color: 'var(--text-secondary)', border: '1px solid var(--border-subtle)', fontFamily: 'var(--font-ui)' }}>
                    {item.replace(/_/g, ' ')}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Drop zone */}
          <div
            onDragOver={e => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={e => { e.preventDefault(); setDragOver(false); uploadFiles(e.dataTransfer.files); }}
            onClick={() => fileInputRef.current?.click()}
            style={{
              border: `2px dashed ${dragOver ? 'var(--accent-primary)' : 'var(--border-default)'}`,
              borderRadius: '12px', padding: '28px 24px', textAlign: 'center',
              cursor: 'pointer', background: dragOver ? 'var(--accent-glow)' : 'transparent',
              marginBottom: showSourceLibrary ? '24px' : '16px', transition: 'all 0.15s',
            }}
          >
            <Upload size={24} style={{ color: dragOver ? 'var(--accent-primary)' : 'var(--text-muted)', margin: '0 auto 8px', display: 'block' }} />
            <p style={{ fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)', fontFamily: 'var(--font-ui)', margin: '0 0 4px' }}>
              Drop files here or click to upload
            </p>
            <p style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', margin: 0 }}>
              PDF, Word, images · Page-aware extraction · Matter-linked retrieval · Indian-language support built in
            </p>
          </div>
          <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.doc,.jpg,.jpeg,.png,.tiff,.webp,.txt" style={{ display: 'none' }} onChange={e => { uploadFiles(e.target.files); e.target.value = ''; }} />

          {/* Upload progress indicators */}
          {uploading.length > 0 && (
            <div style={{ marginBottom: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {uploading.map(u => (
                <div key={u.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: 'var(--bg-surface)', borderRadius: '8px', border: '1px solid var(--border-subtle)' }}>
                  <Loader2 size={13} className="animate-spin" style={{ color: 'var(--accent-primary)', flexShrink: 0 }} />
                  <span style={{ fontSize: '12px', color: 'var(--text-secondary)', fontFamily: 'var(--font-ui)', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.name}</span>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', flexShrink: 0 }}>Uploading…</span>
                </div>
              ))}
            </div>
          )}

          {!showSourceLibrary && files.length > 0 && (
            <div className="advoq-card p-4" style={{ marginBottom: '16px' }}>
              <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <p className="text-sm font-semibold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--font-display)' }}>
                    Source library stays compact on landing
                  </p>
                  <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>
                    Open the full file list only when you want to browse, search, or manage documents.
                  </p>
                </div>
                <button type="button" className="advoq-btn-secondary text-sm" onClick={() => setShowSourceLibrary(true)}>
                  Browse sources
                </button>
              </div>
            </div>
          )}

          {/* Filters */}
          {showSourceLibrary && files.length > 0 && (
            <div style={{ display: 'flex', gap: '8px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              {/* Search */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: '7px', padding: '6px 10px', flex: '1 1 180px', minWidth: '150px' }}>
                <Search size={13} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                <input value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search files…"
                  style={{ flex: 1, background: 'transparent', border: 'none', outline: 'none', color: 'var(--text-primary)', fontSize: '12px', fontFamily: 'var(--font-ui)' }} />
                {searchQuery && <button onClick={() => setSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex' }}><X size={12} /></button>}
              </div>

              {/* Type filter buttons */}
              {['all', 'pdf', 'image', 'word'].map(t => (
                <button key={t} onClick={() => setFilterType(t)} style={{
                  padding: '5px 12px', borderRadius: '6px', fontSize: '11px', cursor: 'pointer', fontFamily: 'var(--font-ui)', fontWeight: filterType === t ? 600 : 400,
                  border: filterType === t ? 'none' : '1px solid var(--border-default)',
                  background: filterType === t ? 'var(--accent-primary)' : 'var(--bg-surface)',
                  color: filterType === t ? 'var(--accent-foreground)' : 'var(--text-secondary)',
                }}>
                  {t === 'all' ? 'All' : t === 'pdf' ? 'PDF' : t === 'image' ? 'Images' : 'Word'}
                </button>
              ))}

              {/* Language dropdown */}
              {allLanguages.length > 1 && (
                <select value={filterLang} onChange={e => setFilterLang(e.target.value)} style={selectStyle}>
                  <option value="">All languages</option>
                  {allLanguages.map(l => <option key={l} value={l}>{LANG_LABELS[l] || l}</option>)}
                </select>
              )}

              {/* Doc type dropdown */}
              {allDocTypes.length > 1 && (
                <select value={filterDocType} onChange={e => setFilterDocType(e.target.value)} style={selectStyle}>
                  <option value="">All types</option>
                  {allDocTypes.map(d => <option key={d} value={d}>{d.replace('_', ' ')}</option>)}
                </select>
              )}

              {/* Sort */}
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={selectStyle}>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="type">By Type</option>
              </select>
            </div>
          )}

          {/* Loading */}
          {loading && (
            <KnowledgeFileSkeleton />
          )}

          {/* Empty state */}
          {!loading && files.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <BookOpen className="w-12 h-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">Upload your first document</h3>
              <p className="text-sm text-gray-400 max-w-xs">
                Add FIRs, judgments, contracts, affidavits, or chamber notes so Advoq can retrieve, cite, and reason from your live file set.
              </p>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="mt-4 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors inline-flex items-center gap-2"
              >
                <Upload size={14} />
                Add Sources
              </button>
            </div>
          )}

          {/* No results */}
          {!loading && showSourceLibrary && files.length > 0 && filtered.length === 0 && (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <Search className="w-12 h-12 text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600 mb-2">No files match these filters</h3>
              <p className="text-sm text-gray-400 max-w-xs">
                Clear one or two filters to widen the source library, or upload a new record if this matter still needs support.
              </p>
            </div>
          )}

          {/* File list */}
          {!loading && showSourceLibrary && filtered.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {filtered.map(f => (
                <FileCard
                  key={f.file_id}
                  file={f}
                  onDelete={(id, name) => setDeleteConfirm({ id, name })}
                  onRetry={retryFile}
                  onView={setDetailFile}
                  onPreview={openPreview}
                  displayLanguage={normalizeDisplayLanguage(user?.language)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Delete confirmation modal */}
        {deleteConfirm && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60 }}>
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: '12px', padding: '24px', maxWidth: '360px', width: '90%' }}>
              <p style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: '15px', color: 'var(--text-primary)', marginBottom: '8px' }}>Move file to trash?</p>
              <p style={{ fontFamily: 'var(--font-ui)', fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '20px' }}>
                "{deleteConfirm.name}" will leave the knowledge base now and stay available for permanent deletion in Settings.
              </p>
              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                <button onClick={() => setDeleteConfirm(null)} style={{ padding: '7px 16px', borderRadius: '7px', border: '1px solid var(--border-default)', background: 'transparent', color: 'var(--text-secondary)', fontSize: '13px', cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>Cancel</button>
                <button onClick={() => deleteFile(deleteConfirm.id, deleteConfirm.name)} style={{ padding: '7px 16px', borderRadius: '7px', border: 'none', background: '#ef4444', color: '#fff', fontSize: '13px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-ui)' }}>Move to trash</button>
              </div>
            </div>
          </div>
        )}

        {/* File detail panel */}
        {detailFile && (
          <FileDetailPanel
            file={detailFile}
            onClose={() => setDetailFile(null)}
            onPreview={openPreview}
            onFileUpdated={(updated) => {
              setFiles(prev => prev.map(f => f.file_id === updated.file_id ? { ...f, ...updated } : f));
              setDetailFile(updated);
            }}
          />
        )}

        {previewUrl && (
          <LinkPreviewPanel
            url={previewUrl}
            title={previewTitle}
            onClose={() => { setPreviewUrl(''); setPreviewTitle(''); }}
          />
        )}
      </main>
    </div>
  );
}
